import React, { useEffect, useRef, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, SectionList, ActivityIndicator } from 'react-native-web';
import * as Animatable from 'react-native-animatable';
import moment from 'moment';
import { useRoute } from '@react-navigation/native';
import { cloneDeep } from 'lodash';
import Icon from 'react-native-vector-icons/dist/MaterialIcons';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';

// Child Component
import { TimeOffModal } from './component_time_off_modal';
import { GoogleLoginModal } from './component_google_login';

// Custom Components
import { EmptyTextSection, NavBar } from '../../components';
import LoadingView from '../../components/component_loading_view';

// APIs
import { getFoodTruckCalendar } from '../../modules';

// Converters
import { checkDateArray } from '../../modules/converters';

// Utils
import { generateColor, showAlert, showAppError, toTitleCase } from '../../utils/util_helpers';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';
import { toast } from 'react-hot-toast';
import { AutoSyncModal } from './component_auto_sync_modal';
import ImportModal from './component_import_modal';
import { promptRemoveEvent } from '../../utils/util_events';
import { getTruckScheduleIFrame } from '../../utils/util_truck_helpers';

export function ProfileSchedule() {
    const {
        onUpdated = () => {},
        onTimeOffUpdated = () => {},
        shouldOpenTimeOff = false
    } = useRoute()?.params || {};

    // region Refs
    const calendarView = useRef(null);
    const calendarComp = useRef(null);
    const calendarEventView = useRef(null);
    const listView = useRef(null);
    const timeOffModal = useRef(null);

    const openTimeOff = useRef(shouldOpenTimeOff);
    const currentMonth = useRef(null);

    const autoSyncModal = useRef(null);
    const googleModal = useRef(null);
    const importModal = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData, setGlobalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [isReloadingCalendar, setReloadingCalendar] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [isDeletingEvent, setDeletingEvent] = useState(false);

    const [currentOffReasonID, setCurrentOffReasonID] = useState(-1);
    const [googleData, setGoogleData] = useState({ isSyncOn: false });
    const [returnDate, setReturnDate] = useState(moment().unix());

    const [calendarObj, setCalendarObj] = useState({});
    const [selectedDateString, setSelectedDateString] = useState('');
    const [truckSchedule, setTruckSchedule] = useState([]);
    const [iFrameCodeOpened, setIFrameCodeOpened] = useState(false);
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => goFetchTruckCalendar(), 500);
    }, []);
    // endregion

    // region Function - Go Fetch Truck Calendar
    const goFetchTruckCalendar = (callback = () => {}) => {
        setReloadingCalendar(true);
        getFoodTruckCalendar(globalTruckData.id)
            .then((result) => {
                const updatedTruckData = cloneDeep(globalTruckData);
                updatedTruckData.calendar = result.data;

                // Process data
                initializeData(updatedTruckData, callback);
                setGlobalTruckData(updatedTruckData);
            })
            .catch((error) => showAppError(error))
            .finally(() => {
                setLoading(false);
                setReloadingCalendar(false);
            })
    }
    // endregion

    // region Function - Sort Truck Schedule
    const sortTruckSchedule = (truckSchedule) => {
        let tempArr = JSON.parse(JSON.stringify(truckSchedule));

        tempArr
            // Sort months
            .sort((a, b) => moment(a.title, 'MMMM YYYY').unix() - moment(b.title, 'MMMM YYYY').unix())
            // Sort events in each month
            .forEach((month) => month.data.sort((a, b) => a.timeObj.startTime - b.timeObj.startTime));

        return(tempArr);
    }
    // endregion

    // region Function - Reload Google Data
    const reloadGoogleData = () => {
        if (GLOBAL.DATA.USER['google_email'] && GLOBAL.DATA.USER['google_refresh_token']) {
            setGoogleData({
                email: GLOBAL.DATA.USER['google_email'],
                selectedCalendar: { name: GLOBAL.DATA.USER['sync_calendar_name'], email: GLOBAL.DATA.USER['sync_calendar_email'] },
                syncOn: !!GLOBAL.DATA.USER['is_sync_calendar']
            });
        }
        else setGoogleData(null);
    }
    // endregion

    // region Function - Initialize Data
    const initializeData = (customData = null, callback = () => {}) => {
        const usingTruckData = customData || globalTruckData;
        const calendarObj = {}, calendarCompData = {}, scheduleObj = {};

        // Generate react-native-calendar's component data
        usingTruckData.calendar.forEach((item) => {
            // Generate data for calendar comp
            const timeObjArr = checkDateArray(item);

            timeObjArr.forEach((timeItem) => {
                if (timeItem.endTime < moment().unix()) return;

                // Generate random color for each event
                const tempObj = Object.assign(cloneDeep(item), { timeObj: timeItem, timeObjArr });
                const itemColor = generateColor((tempObj['location_name'] || '[Unknown Event]').replace(/\s/g, ''));
                const startMoment = moment.unix(timeItem.startTime);

                const dotItem = { key: tempObj.id.toString(), color: itemColor, selectedDotColor: 'white' };
                const dateStr = startMoment.format('YYYY-MM-DD');

                if (calendarObj.hasOwnProperty(dateStr)) {
                    if (!calendarCompData[dateStr].dots) calendarCompData[dateStr].dots = [];
                    calendarCompData[dateStr].dots.push(dotItem);
                    calendarObj[dateStr].push(tempObj);
                }
                else {
                    calendarCompData[dateStr] = { dots: [dotItem], selectedColor: GLOBAL.COLOR.MAIN };
                    calendarObj[dateStr] = [tempObj];
                }

                let monthStr = dateStr.substring(0, 8);
                if (!scheduleObj.hasOwnProperty(monthStr)) {
                    scheduleObj[monthStr] = {
                        title: moment(monthStr, 'YYYY-MM').format('MMMM YYYY'),
                        data: [tempObj]
                    };
                }
                else scheduleObj[monthStr].data.push(tempObj);
            });
        });

        // Sort event list view
        const scheduleArr = Object.keys(scheduleObj || {}).map((monthStr) => scheduleObj[monthStr]);
        const newTruckSchedule = sortTruckSchedule(scheduleArr);
        setTruckSchedule(newTruckSchedule);

        // Check if auto sync is on
        reloadGoogleData();

        // Time Off Setting
        const returnDateUnix = moment(usingTruckData['return_date'], 'YYYY-MM-DD').startOf('day').unix();
        setReturnDate(returnDateUnix > moment().unix() ? returnDateUnix : moment().unix());
        setCurrentOffReasonID(usingTruckData['status'] || 1);

        // Set data to state
        setLoading(false);
        setDeletingEvent(false);

        // Open time off if requested
        setTimeout(() => {
            // If user ordered to open the Time Off modal
            if (openTimeOff.current) {
                timeOffModal.current?.showModal();
                openTimeOff.current = false;
            }

            // Callback
            if (callback) callback();
        }, 500);
    }
    // endregion

    // region Function - On New Event Pressed
    const _onNewEventPressed = () => {
        const startMoment = moment();
        const itemData = {
            timeObj: {
                startTime: startMoment.unix(),
                endTime: startMoment.add(1, 'h').unix()
            }
        };

        RootNavigation.push(routeKeys.PROFILE_SCHEDULE_EDIT, {
            eventData: itemData,
            onEventCreated: (eventData) => {
                goFetchTruckCalendar(() => toast('Successfully added event "' + eventData.location_name + '"'));
            }
        });
    };
    // endregion

    // region Function - On Event Item Pressed
    const _onEventItemPressed = (item) => {
        RootNavigation.push(routeKeys.PROFILE_SCHEDULE_EDIT, {
            eventData: item,
            onEventUpdated: (eventData) => {
                goFetchTruckCalendar(() => toast('Successfully updated event "' + eventData.location_name + '"'));
            },
            onEventRemoved: (newTruckData) => {
                initializeData(newTruckData);
                setGlobalTruckData(newTruckData);

                // Update Profile tab state
                onUpdated();
            }
        });
    }
    // endregion

    // region Function - Event Updated
    const goUpdateEvent = (newItem) => {
        const updatedEvent = cloneDeep(newItem);
        const copiedTruckCalendar = cloneDeep(globalTruckData.calendar);
        const itemIndex = copiedTruckCalendar.findIndex((item) => item.id === updatedEvent.id);

        // Replace old item with updated data
        copiedTruckCalendar.splice(itemIndex, 1, updatedEvent);

        // Save to state
        const newTruckData = Object.assign(cloneDeep(globalTruckData), { calendar: copiedTruckCalendar });
        initializeData(
            newTruckData,
            () => {
                // Update Profile tab
                onUpdated();

                // Notify user
                toast('Successfully updated event "' + newItem.location_name + '"');
            }
        );
        setGlobalTruckData(newTruckData);
    }
    // endregion

    // region Function - Remove Event
    const _onRemoveEventPressed = (eventData, callback = () => {}) => {
        promptRemoveEvent({
            eventData,
            onDeletePressed: () => setDeletingEvent(true),
            onDeleteCompleted: (newEventResult = {}, type = 3) => {
                setDeletingEvent(false);

                // Generate new calendar obj
                const newCalendarArr = cloneDeep(globalTruckData.calendar);
                const eventIndex = newCalendarArr.findIndex((item) => item.id === eventData.id);
                if (type === 3) {
                    newCalendarArr.splice(eventIndex, 1);
                }
                else newCalendarArr.splice(eventIndex, 1, newEventResult);

                // Update scene state
                const newTruckData = Object.assign(cloneDeep(globalTruckData), { calendar: newCalendarArr });
                initializeData(newTruckData);
                setGlobalTruckData(newTruckData)

                // Update Profile tab state
                onUpdated();

                // Callback for Event Detail screen
                callback(null);
            },
            onDeleteError: (error) => {
                setDeletingEvent(false);
                showAppError(error);
            }
        });
    }
    // endregion

    // region Render Section Item
    const renderSectionItem = (item) => (
        <View style={{
            borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: 8,
            width: '100%', backgroundColor: 'white', flexDirection: 'row', alignItems: 'center'
        }}>
            <TouchableOpacity
                onPress={() => _onEventItemPressed(item)}
                style={{ flex: 1, flexDirection: 'row', alignItems: 'center', padding: 24 }}>
                {/* Date String */}
                <View style={{ alignItems: 'center' }}>
                    {/* Month */}
                    <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, fontSize: 16 }}>
                        {moment.unix(item.timeObj.startTime).format('MMM')}
                    </Text>

                    {/* Year */}
                    <Text style={{ fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 24, letterSpacing: -0.2, marginTop: 8 }}>
                        {moment.unix(item.timeObj.startTime).format('DD')}
                    </Text>
                </View>

                {/* Event Info */}
                <View style={{ flex: 1, marginLeft: 24, justifyContent: 'flex-start' }}>
                    {/* Time */}
                    <Text style={{ fontSize: 16, fontWeight: '500', color: GLOBAL.COLOR.MAIN }}>
                        {moment.unix(item.timeObj.startTime).format('h:mm A') + ' - ' + moment.unix(item.timeObj.endTime).format('h:mm A')}
                    </Text>

                    {/* Location Name */}
                    <Text style={{ fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 20, marginTop: 8 }} numberOfLines={1}>
                        {item['location_name']}
                    </Text>

                    {/* Address */}
                    <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16, marginTop: 8 }} numberOfLines={1}>
                        {item['address']}
                    </Text>
                </View>
            </TouchableOpacity>

            {/* Option and Location Warning */}
            <View style={{ alignItems: 'flex-end', paddingRight: 16 }}>
                {/* Warning Icon */}
                {
                    (Number(item.latitude) === 0 || Number(item.latitude) === -1) &&
                    <TouchableOpacity
                        onPress={() => showAlert({ text: 'Events without an address will not show up on Truckster.<br/><br/>Please add one ASAP.' })}
                        style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'row', paddingRight: 16, paddingVertical: 8 }}>
                        <Icon
                            name={'warning'}
                            size={24}
                            color={GLOBAL.COLOR.BASIC.GOLD}
                        />

                        <Text style={{ color: GLOBAL.COLOR.BASIC.GOLD, fontWeight: '500', marginLeft: 8, fontSize: 14 }}>
                            No Location
                        </Text>
                    </TouchableOpacity>
                }

                {/* Edit/Delete */}
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {/* Edit Button */}
                    <TouchableOpacity
                        onPress={() => _onEventItemPressed(item)}
                        style={{ width: 48, height: 48, alignItems: 'center', justifyContent: 'center' }}>
                        <FeatherIcon name={'edit'} color={GLOBAL.COLOR.TEXT_GRAY} size={24} />
                    </TouchableOpacity>

                    {/* Delete Button */}
                    <TouchableOpacity
                        onPress={() => _onRemoveEventPressed(item)}
                        style={{ width: 48, height: 48, alignItems: 'center', justifyContent: 'center', marginLeft: 8 }}>
                        <FeatherIcon name={'trash'} color={'red'} size={24} />
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    )
    // endregion

    // region Render Calendar Event View
    const renderCalendarEventView = () => {
        const selectedDateData = calendarObj[selectedDateString] || [];
        return(
            <Animatable.View
                ref={calendarEventView}
                animation={'fadeZoom'}
                duration={400}
                style={{ backgroundColor: 'white', flex: 1, width: '100%' }}>
                {
                    (selectedDateData.length > 0) ?
                        <FlatList
                            data={selectedDateData}
                            style={{ flex: 1 }}
                            contentContainerStyle={{ paddingBottom: 108 }}
                            keyExtractor={(item) => item.id?.toString()}
                            renderItem={({ item }) => renderSectionItem(item)}
                        />
                        :
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 36 }}>
                            <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 32, textAlign: 'center', fontSize: 16, marginBottom: 24 }}>
                                No upcoming events for this date,
                                {'\n'}tap the below button add one!
                            </Text>
                        </View>
                }
            </Animatable.View>
        );
    }
    // endregion

    // region Render Section Header
    const renderSectionHeader = (text) => (
        <View style={{
            width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY,
            paddingHorizontal: 24, paddingBottom: 16, paddingTop: 32
        }}>
            <Text style={{ color: 'black', fontSize: 24, letterSpacing: -0.2, fontWeight: '900' }}>
                {toTitleCase(text)}
            </Text>
        </View>
    )
    // endregion

    // region Render List View
    const renderListView = () => (
        <Animatable.View
            ref={listView}
            animation={'fadeZoom'} duration={400}
            style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
            {
                truckSchedule.length > 0 ?
                    <SectionList
                        animation={'fadeIn'}
                        style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                        showsVerticalScrollIndicator={true}
                        contentContainerStyle={{ paddingBottom: 24 + 56, paddingHorizontal: '15%' }}
                        sections={truckSchedule}
                        renderItem={({ item }) => renderSectionItem(item)}
                        renderSectionHeader={({ section }) => renderSectionHeader(section.title)}
                        keyExtractor={(item, index) => index.toString()}
                        removeClippedSubviews={true}
                        maxToRenderPerBatch={15}
                        ItemSeparatorComponent={() => <View style={{ height: 8, width: 1 }}/>}
                    />
                    :
                    <EmptyTextSection
                        title={'No upcoming events'}
                        subTitle={'Tap the below button to create one or hit\n"Import" to get events from your Google Calendar'}
                    />
            }
        </Animatable.View>
    )
    // endregion

    // region Render Top Section Button
    const renderTopButton = (text, icon, _onPress = () => {}, containerStyle = {}, textStyle = {}, isSpinning = false) => (
        <TouchableOpacity
            disabled={isSpinning}
            onPress={_onPress}
            style={[{
                flexDirection: 'row', alignItems: 'center', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY,
                paddingHorizontal: 24, paddingVertical: 12, borderRadius: 24, marginHorizontal: 4
            }, containerStyle]}>
            {
                isSpinning ?
                    <ActivityIndicator color={GLOBAL.COLOR.BASIC.GRAY} size={24} />
                    :
                    (icon && <FeatherIcon color={textStyle?.color || GLOBAL.COLOR.BASIC.BLACK_TWO} size={24} name={icon} />)
            }

            <Text style={[{ color: isSpinning ? GLOBAL.COLOR.BASIC.GRAY : GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 16, fontWeight: '500', marginLeft: (icon && text) ? 8 : 0 }, textStyle]}>
                {text}
            </Text>
        </TouchableOpacity>
    )
    // endregion

    // region Render Add Item
    const renderAddItemButton = () => (
        <Animatable.View
            animation={'zoomIn'}
            delay={500}
            duration={600}
            style={{ position: 'absolute', bottom: 24, right: 36 }}>
            <TouchableOpacity
                onPress={() => _onNewEventPressed()}
                style={{
                    backgroundColor: 'black', borderRadius: 12,
                    alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1',
                    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16
                }}>
                <FeatherIcon name={'plus'} size={24} color={'white'} />
                <Text style={{ color: 'white', fontSize: 18, fontWeight: '500', marginLeft: 8 }}>
                    New Event
                </Text>
            </TouchableOpacity>
        </Animatable.View>
    )
    // endregion

    // region Render iFrame Section
    const renderIFrameSection = () => {
        const code = getTruckScheduleIFrame(globalTruckData);
        return(
            <View style={{ width: '35%', borderTopWidth: iFrameCodeOpened ? 1 : 0, borderColor: GLOBAL.COLOR.DIVIDER, marginTop: 32, alignSelf: 'center' }}>
                {
                    iFrameCodeOpened ?
                        <TouchableOpacity
                            onPress={() => setIFrameCodeOpened(false)}
                            style={{ width: '100%', paddingHorizontal: 16, paddingVertical: 24 }}>
                            {/* Instructions */}
                            <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontSize: 16, lineHeight: 20, textAlign: 'center' }}>
                                Tap the textbox to copy the iFrame code, then insert it into your website's source code where you want the calendar to be.
                                {'\n\n'}Not sure what that means? Reach out to us at{' '}
                                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontSize: 16, fontWeight: 'bold', lineHeight: 20 }}>
                                    vendor.support@gotruckster.com
                                </Text>
                            </Text>

                            {/* Code */}
                            <TouchableOpacity
                                onPress={() => {
                                    navigator.clipboard.writeText(code).then(() => showAlert({ text: 'Code copied to your clipboard!' }));
                                }}
                                style={{
                                    alignSelf: 'stretch', paddingVertical: 16, paddingHorizontal: 24, marginTop: 24,
                                    borderRadius: 8, borderWidth: 1, borderColor: GLOBAL.COLOR.BASIC.GRAY,
                                    backgroundColor: GLOBAL.COLOR.BASIC.LIGHT_GRAY
                                }}>
                                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 16, fontFamily: 'courier', lineHeight: 28, textAlign: 'left' }}>
                                    {code}
                                </Text>
                            </TouchableOpacity>
                        </TouchableOpacity>
                        :
                        <TouchableOpacity
                            onPress={() => setIFrameCodeOpened(true)}
                            style={{
                                width: '100%', height: 56, backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE,
                                alignItems: 'center', justifyContent: 'center',
                                paddingHorizontal: 24, borderTopLeftRadius: 8, borderTopRightRadius: 8
                            }}>
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold' }}>
                                Add Truckster Calendar to your website!
                            </Text>
                        </TouchableOpacity>
                }
            </View>
        );
    }
    // endregion

    // region MAIN RENDER
    const isSyncOn = googleData?.syncOn, isTimeOff = (Number(currentOffReasonID) !== 1);
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <NavBar
                title={'Food Truck Schedule'}
                leftButtonIconName={'keyboard-arrow-left'}
                leftButtonIconSize={40}
                hideBorder={!isLoading}
            />

            {/* Top Section */}
            {
                !isLoading &&
                <Animatable.View
                    animation={'fadeIn'}
                    style={{
                        width: '100%', backgroundColor: 'white', paddingTop: 8,
                        borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER
                    }}>
                    {/* Buttons */}
                    <Animatable.View
                        animation={'fadeIn'}
                        style={{ width: '100%', backgroundColor: 'white', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {!isSyncOn && renderTopButton('Import', 'download', () => importModal.current?.showModal())}
                        {renderTopButton(`Auto-Sync: ${isSyncOn ? 'On' : 'Off'}`, 'cloud', () => autoSyncModal.current?.showModal(), isSyncOn ? { backgroundColor: GLOBAL.COLOR.BASIC.UI_GREEN } : {}, isSyncOn ? { color: 'white' } : {})}
                        {renderTopButton('Time Off', 'wifi-off', () => timeOffModal.current?.showModal(), isTimeOff ? { backgroundColor: GLOBAL.COLOR.BASIC.UI_YELLOW } : {}, isTimeOff ? { color: 'white' } : {})}
                        {renderTopButton('Refresh', 'refresh-cw', () => goFetchTruckCalendar(), {}, {}, isReloadingCalendar)}
                    </Animatable.View>

                    {/* Auto-sync Notice */}
                    {
                        isSyncOn &&
                        <Text style={{ textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, marginTop: 24, marginBottom: 8, lineHeight: 18 }}>
                            If you recently added new events to your Google Calendar, wait for next auto-sync cycle for them to appear here
                        </Text>
                    }

                    {/* iFrame Section */}
                    {renderIFrameSection()}
                </Animatable.View>
            }

            {/* Content */}
            {
                isLoading ?
                    <LoadingView />
                    :
                    <View style={{ flex: 1, width: '100%' }}>
                        {/* Event List */}
                        {renderListView()}

                        {/* Add Button */}
                        {renderAddItemButton()}

                        {/* Loading Overlay */}
                        {isDeletingEvent && <LoadingView isAbsolute={true}/>}
                    </View>
            }

            {/* Time Off Modal */}
            <TimeOffModal
                ref={timeOffModal}
                returnDate={returnDate}
                currentOffReasonID={currentOffReasonID}
                onTimeOffUpdated={(newReturnDate, newCurrentOffReasonID) => {
                    setReturnDate(newReturnDate);
                    setCurrentOffReasonID(newCurrentOffReasonID);

                    // Callbacks
                    const newTruckData = Object.assign(cloneDeep(globalTruckData), { status: newCurrentOffReasonID, return_date: moment.unix(newReturnDate).format('YYYY-MM-DD') });
                    setGlobalTruckData(newTruckData);
                    onTimeOffUpdated(cloneDeep(newTruckData));
                }}
            />

            {/* Auto-Sync Modal */}
            <AutoSyncModal
                ref={autoSyncModal}
                openGoogleLoginModalFunc={(callback) => googleModal.current?.showModal(callback)}
                onGoogleDataUpdated={() => reloadGoogleData()}
            />

            {/* Import Modal */}
            <ImportModal
                ref={importModal}
                openGoogleLoginModalFunc={(callback) => googleModal.current?.showModal(callback)}
                onImportDone={(resultList) => {
                    const newCalendar = globalTruckData.calendar.concat(resultList);
                    const newTruckData = Object.assign(cloneDeep(globalTruckData), { calendar: newCalendar });
                    initializeData(
                        newTruckData,
                        () => {
                            // Callback for Profile tab
                            onUpdated();

                            // Show toast
                            toast(`${resultList.length > 1 ? 'All selected events have' : 'New event has'} been imported successfully to your Truckster schedule`, { duration: 3000 });
                        }
                    );
                    setGlobalTruckData(newTruckData);
                }}
            />

            {/* Google Login Modal */}
            <GoogleLoginModal
                ref={googleModal}
                onGoogleDataUpdated={() => reloadGoogleData()}
            />
        </View>
    );
    // endregion
}
